import React from 'react'
import { motion } from 'framer-motion'
import { 
  Shield,
  AlertTriangle,
  Eye,
  EyeOff,
  Monitor,
  Code
} from 'lucide-react'

interface AntiPiracyDetectorProps {
  onDetection: (reason: string) => void
  enabled: boolean
}

export const AntiPiracyDetector: React.FC<AntiPiracyDetectorProps> = ({
  onDetection,
  enabled
}) => {
  const [detectionStatus, setDetectionStatus] = React.useState({
    screenRecording: false,
    developerTools: false,
    suspiciousActivity: false,
    warnings: [] as string[]
  })

  const [isMonitoring, setIsMonitoring] = React.useState(false)

  // Screen recording detection
  React.useEffect(() => {
    if (!enabled) return

    let screenRecordingInterval: NodeJS.Timeout

    const detectScreenRecording = async () => {
      try {
        // Method 1: Check for screen capture API usage
        if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
          // Monitor for screen sharing requests
          const originalGetDisplayMedia = navigator.mediaDevices.getDisplayMedia.bind(navigator.mediaDevices)
          navigator.mediaDevices.getDisplayMedia = function(...args) {
            setDetectionStatus(prev => ({ ...prev, screenRecording: true }))
            onDetection('Screen recording detected via getDisplayMedia API')
            return originalGetDisplayMedia(...args)
          }
        }

        // Method 2: Check for common screen recording software patterns
        const checkForRecordingSoftware = () => {
          const suspiciousPatterns = [
            'OBS Studio',
            'Camtasia',
            'Bandicam',
            'Fraps',
            'XSplit',
            'Streamlabs',
            'FFmpeg'
          ]

          const userAgent = navigator.userAgent
          const suspiciousFound = suspiciousPatterns.some(pattern => 
            userAgent.toLowerCase().includes(pattern.toLowerCase())
          )

          if (suspiciousFound) {
            setDetectionStatus(prev => ({ 
              ...prev, 
              screenRecording: true,
              warnings: [...prev.warnings, 'Potential recording software detected']
            }))
            onDetection('Potential screen recording software detected in user agent')
          }
        }

        // Method 3: Monitor for screen capture events
        const detectScreenCapture = () => {
          // Check if screen capture is active (experimental)
          if ('getDisplayMedia' in navigator.mediaDevices) {
            // Create a hidden video element to test screen capture
            const video = document.createElement('video')
            video.style.display = 'none'
            document.body.appendChild(video)

            navigator.mediaDevices.getDisplayMedia({ video: true })
              .then(stream => {
                setDetectionStatus(prev => ({ ...prev, screenRecording: true }))
                onDetection('Active screen capture detected')
                stream.getTracks().forEach(track => track.stop())
                document.body.removeChild(video)
              })
              .catch(() => {
                // Expected - user denied or no screen capture active
                document.body.removeChild(video)
              })
          }
        }

        checkForRecordingSoftware()
        
        // Periodic checks for screen recording
        screenRecordingInterval = setInterval(() => {
          checkForRecordingSoftware()
        }, 5000)

      } catch (error) {
        console.warn('Screen recording detection failed:', error)
      }
    }

    detectScreenRecording()

    return () => {
      if (screenRecordingInterval) {
        clearInterval(screenRecordingInterval)
      }
    }
  }, [enabled, onDetection])

  // Developer tools detection
  React.useEffect(() => {
    if (!enabled) return

    let devToolsInterval: NodeJS.Timeout
    let devToolsOpen = false

    const detectDevTools = () => {
      const threshold = 160
      const widthThreshold = window.outerWidth - window.innerWidth > threshold
      const heightThreshold = window.outerHeight - window.innerHeight > threshold

      // Method 1: Check for size differences
      if (widthThreshold || heightThreshold) {
        if (!devToolsOpen) {
          devToolsOpen = true
          setDetectionStatus(prev => ({ 
            ...prev, 
            developerTools: true,
            warnings: [...prev.warnings, 'Developer tools opened']
          }))
          onDetection('Developer tools detected (window size method)')
        }
      } else {
        devToolsOpen = false
      }

      // Method 2: Check for console tampering
      let consoleOpened = false
      const originalLog = console.log
      console.log = function(...args) {
        if (!consoleOpened) {
          consoleOpened = true
          setDetectionStatus(prev => ({ 
            ...prev, 
            developerTools: true,
            warnings: [...prev.warnings, 'Console access detected']
          }))
          onDetection('Console access detected')
        }
        return originalLog.apply(console, args)
      }

      // Method 3: Performance timing detection
      const start = performance.now()
      console.clear()
      const end = performance.now()
      
      if (end - start > 100) {
        if (!devToolsOpen) {
          devToolsOpen = true
          setDetectionStatus(prev => ({ 
            ...prev, 
            developerTools: true,
            warnings: [...prev.warnings, 'Developer tools detected via timing']
          }))
          onDetection('Developer tools detected (performance timing)')
        }
      }
    }

    // Check immediately and then periodically
    detectDevTools()
    devToolsInterval = setInterval(detectDevTools, 1000)

    return () => {
      if (devToolsInterval) {
        clearInterval(devToolsInterval)
      }
    }
  }, [enabled, onDetection])

  // Suspicious activity detection
  React.useEffect(() => {
    if (!enabled) return

    const detectSuspiciousActivity = () => {
      // Method 1: Rapid key combinations
      let keySequence: string[] = []
      const suspiciousKeys = ['F12', 'F11', 'ControlLeft', 'ShiftLeft', 'KeyI', 'KeyJ', 'KeyU']

      const handleKeyDown = (event: KeyboardEvent) => {
        keySequence.push(event.code)
        if (keySequence.length > 5) {
          keySequence = keySequence.slice(-5)
        }

        // Detect common dev tools shortcuts
        if ((event.ctrlKey && event.shiftKey && event.key === 'I') ||
            (event.ctrlKey && event.shiftKey && event.key === 'J') ||
            (event.ctrlKey && event.key === 'U') ||
            event.key === 'F12') {
          setDetectionStatus(prev => ({ 
            ...prev, 
            suspiciousActivity: true,
            warnings: [...prev.warnings, `Suspicious key combination: ${event.key}`]
          }))
          onDetection(`Suspicious keyboard shortcut detected: ${event.key}`)
        }
      }

      // Method 2: Right-click context menu
      const handleContextMenu = (event: MouseEvent) => {
        setDetectionStatus(prev => ({ 
          ...prev, 
          suspiciousActivity: true,
          warnings: [...prev.warnings, 'Context menu accessed']
        }))
        onDetection('Right-click context menu accessed')
        event.preventDefault()
      }

      // Method 3: Tab visibility changes
      const handleVisibilityChange = () => {
        if (document.hidden) {
          setDetectionStatus(prev => ({ 
            ...prev, 
            warnings: [...prev.warnings, 'Tab became hidden']
          }))
          // Don't trigger detection for normal tab switching
        }
      }

      // Method 4: Debugger detection
      const detectDebugger = () => {
        const start = new Date().getTime()
        debugger
        const end = new Date().getTime()
        
        if (end - start > 100) {
          setDetectionStatus(prev => ({ 
            ...prev, 
            developerTools: true,
            warnings: [...prev.warnings, 'Debugger breakpoint detected']
          }))
          onDetection('Debugger detected')
        }
      }

      document.addEventListener('keydown', handleKeyDown)
      document.addEventListener('contextmenu', handleContextMenu)
      document.addEventListener('visibilitychange', handleVisibilityChange)
      
      // Periodic debugger check
      const debuggerInterval = setInterval(detectDebugger, 3000)

      return () => {
        document.removeEventListener('keydown', handleKeyDown)
        document.removeEventListener('contextmenu', handleContextMenu)
        document.removeEventListener('visibilitychange', handleVisibilityChange)
        clearInterval(debuggerInterval)
      }
    }

    const cleanup = detectSuspiciousActivity()
    setIsMonitoring(true)

    return cleanup
  }, [enabled, onDetection])

  // Clipboard protection
  React.useEffect(() => {
    if (!enabled) return

    const protectClipboard = () => {
      const handleCopy = (event: ClipboardEvent) => {
        setDetectionStatus(prev => ({ 
          ...prev, 
          suspiciousActivity: true,
          warnings: [...prev.warnings, 'Copy operation attempted']
        }))
        onDetection('Copy operation detected')
        event.preventDefault()
        return false
      }

      const handleCut = (event: ClipboardEvent) => {
        setDetectionStatus(prev => ({ 
          ...prev, 
          suspiciousActivity: true,
          warnings: [...prev.warnings, 'Cut operation attempted']
        }))
        onDetection('Cut operation detected')
        event.preventDefault()
        return false
      }

      document.addEventListener('copy', handleCopy)
      document.addEventListener('cut', handleCut)

      return () => {
        document.removeEventListener('copy', handleCopy)
        document.removeEventListener('cut', handleCut)
      }
    }

    return protectClipboard()
  }, [enabled, onDetection])

  if (!enabled) {
    return null
  }

  return (
    <motion.div 
      className="fixed bottom-4 right-4 z-50"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
    >
      <div className="bg-cosmic-dark/90 backdrop-blur-md border border-stellar-cyan/30 rounded-lg p-3 max-w-sm">
        <div className="flex items-center space-x-2 mb-2">
          <Shield className="h-4 w-4 text-stellar-cyan" />
          <span className="text-sm font-semibold text-white">Anti-Piracy Shield</span>
          <div className={`w-2 h-2 rounded-full ${isMonitoring ? 'bg-green-400' : 'bg-red-400'}`} />
        </div>
        
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="flex items-center space-x-1">
            <Monitor className={`h-3 w-3 ${detectionStatus.screenRecording ? 'text-red-400' : 'text-green-400'}`} />
            <span className="text-stellar-cyan/80">Screen</span>
          </div>
          
          <div className="flex items-center space-x-1">
            <Code className={`h-3 w-3 ${detectionStatus.developerTools ? 'text-red-400' : 'text-green-400'}`} />
            <span className="text-stellar-cyan/80">DevTools</span>
          </div>
          
          <div className="flex items-center space-x-1">
            <Eye className={`h-3 w-3 ${detectionStatus.suspiciousActivity ? 'text-red-400' : 'text-green-400'}`} />
            <span className="text-stellar-cyan/80">Activity</span>
          </div>
        </div>

        {detectionStatus.warnings.length > 0 && (
          <div className="mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded">
            <div className="flex items-center space-x-1 mb-1">
              <AlertTriangle className="h-3 w-3 text-red-400" />
              <span className="text-xs font-semibold text-red-400">Security Alerts</span>
            </div>
            <div className="text-xs text-red-300 space-y-1">
              {detectionStatus.warnings.slice(-3).map((warning, index) => (
                <div key={index}>• {warning}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  )
}